<template>
  <v-dialog v-model="dialog" width="500">
    <v-card class="diary-entry-form" rounded="lg">
      <!-- Header with close button -->
      <div class="d-flex justify-space-between align-center pa-4">
        <div class="d-flex align-center">
          <template v-if="entryType === 'drank'">
            <DrinkIcon :color="settingsStore.settings.buttonColor" class="me-4" />
          </template>
          <template v-else-if="entryType === 'felt'">
            <FeelIcon :color="settingsStore.settings.buttonColor" class="me-4" />
          </template>
          <template v-else-if="entryType === 'did'">
            <DidIcon :color="settingsStore.settings.buttonColor" class="me-4" />
          </template>
          <template v-else-if="entryType === 'exercised'">
            <ExerciseIcon :color="settingsStore.settings.buttonColor" class="me-4" />
          </template>
          <v-icon v-else :icon="entryIcon" size="24" :color="settingsStore.settings.buttonColor" class="me-4"></v-icon>
          <span class="text-h6" :style="{ color: settingsStore.settings.buttonColor }">{{ dynamicLabel }}</span>
        </div>
        <v-btn icon variant="text" @click="closeDialog">
          <v-icon icon="fa fa-times"></v-icon>
        </v-btn>
      </div>

      <v-divider></v-divider>

      <v-card-text class="pa-4">
        <v-alert
          v-if="formError"
          type="error"
          class="mb-4"
        >
          {{ formError }}
        </v-alert>

        <v-form @submit.prevent="submitForm">
          <!-- Date and Time Display -->
          <div class="mb-4">
            <div v-if="!isEditingDateTime" 
                 @click="isEditingDateTime = true"
                 class="text-caption text-grey-darken-1 cursor-pointer d-flex align-center">
              <span>{{ formattedDateTime }}</span>
              <v-icon size="small" class="ms-2">fa fa-pencil</v-icon>
            </div>
            <div v-else class="d-flex gap-4">
              <v-text-field
                v-model="dateValue"
                type="date"
                label="Date"
                variant="outlined"
                density="compact"
                :max="new Date().toISOString().split('T')[0]"
                @blur="checkBlur"
              ></v-text-field>
              <v-text-field
                v-model="timeValue"
                type="time"
                label="Time"
                variant="outlined"
                density="compact"
                @blur="checkBlur"
              ></v-text-field>
            </div>
          </div>

          <!-- Main Entry Field -->
          <v-textarea
            v-model="formData.ate_content"
            :label="dynamicLabel"
            variant="outlined"
            rows="4"
            required
            :rules="[v => !!v || 'This field is required']"
            class="mb-4"
          ></v-textarea>

          <!-- I noticed field -->
          <v-textarea
            v-model="formData.noticed_content"
            :label="noticedLabel"
            :style="`label-color: ${settingsStore.settings.primaryColor}`"
            variant="outlined"
            rows="4"
            placeholder="Optional"
            class="mb-4"
          ></v-textarea>

          <!-- Image Upload Section -->
          <div class="mb-4">
            <div class="text-subtitle-2 mb-2" :style="{ color: settingsStore.settings.primaryColor }">
              Add Photo (Optional)
            </div>
            
            <!-- Existing Image Display -->
            <div v-if="existingImageUrl && !selectedImage" class="mb-3">
              <div class="image-preview-container">
                <img :src="existingImageUrl" alt="Diary entry image" class="existing-image" />
                                <v-btn
                  icon="mdi-close"
                  size="x-small"
                  color="error"
                  class="remove-image-btn"
                  @click="removeExistingImage">
                  ×
                </v-btn>
              </div>
            </div>

            <!-- New Image Preview -->
            <div v-if="selectedImage" class="mb-3">
              <div class="image-preview-container">
                <img :src="imagePreviewUrl" alt="Selected image" class="image-preview" />
                                <v-btn
                  size="x-small"
                  color="error"
                  class="remove-image-btn"
                  @click="removeSelectedImage"
                >
                  ×
                </v-btn>
              </div>
            </div>

            <!-- File Input -->
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="handleImageSelect"
            />
            
                          <v-btn
                v-if="!selectedImage && !existingImageUrl"
                variant="outlined"
                :color="settingsStore.settings.primaryColor"
                @click="fileInput?.click()"
                class="mb-2"
              >
                <v-icon start>fa fa-camera</v-icon>
                Choose Photo
              </v-btn>
              
              <v-btn
                v-else
                variant="outlined"
                :color="settingsStore.settings.primaryColor"
                @click="fileInput?.click()"
                class="mb-2"
              >
                <v-icon start>fa fa-camera</v-icon>
                Change Photo
              </v-btn>
            
            <div class="text-caption text-grey-darken-1">
              Supported formats: JPG, PNG, GIF, WebP (Max 10MB)
            </div>
          </div>

          <!-- Practitioner Comment -->
          <v-card
            v-if="props.existingEntry?.practitioner_comment"
            class="practitioner-comment mb-4"
            variant="flat"
            density="comfortable"
            :style="{
              backgroundColor: hexToRgba(settingsStore.settings.primaryColor, 0.1),
              border: `1px solid ${hexToRgba(settingsStore.settings.primaryColor, 0.2)}`
            }"
          >
            <v-card-title 
              class="text-subtitle-2 pb-1 font-weight-bold"
              :style="{ color: settingsStore.settings.primaryColor }"
            >
              Practitioner Comment
            </v-card-title>
            <v-card-text 
              class="pt-0"
              style="color: black;"
            >
              {{ props.existingEntry.practitioner_comment }}
            </v-card-text>
          </v-card>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          :style="{
            backgroundColor: settingsStore.settings.buttonColor,
            color: 'white'
          }"
          class="text-white primary-button"
          rounded="lg"
          block
          @click="submitForm"
          :disabled="!isFormValid || isSubmitting"
          :loading="isSubmitting"
        >
          {{ buttonText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, watch } from 'vue';
import { useUserStore } from '@/api/store/user';
import { useSettingsStore } from '@/api/store/settings';
import { healthDiaryService, type DiaryEntry } from "@/api/services/healthDiary";
import { hexToRgba } from "@/tools/design";
import DrinkIcon from "@/components/Icons/DrinkIcon.vue";
import FeelIcon from "@/components/Icons/FeelIcon.vue";
import DidIcon from "@/components/Icons/DidIcon.vue";
import ExerciseIcon from "@/components/Icons/ExerciseIcon.vue";

interface Props {
  modelValue: boolean;
  entryType?: string | null;
  existingEntry?: DiaryEntry | null;
  isEditing?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'entry-created': [entryDate: string];
  'entry-updated': [entryDate: string];
}>();
const settingsStore = useSettingsStore();

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const formData = ref<Omit<DiaryEntry, 'id' | 'read_by_practitioner'>>({
  entry_datetime: new Date().toISOString(),
  entry_type: '',
  ate_content: '',
  noticed_content: ''
});

  const isSubmitting = ref(false);
  const formError = ref<string | null>(null);
  const selectedImage = ref<File | null>(null);
  const imagePreviewUrl = ref<string>('');
  const removeExistingImageFlag = ref(false);
  const fileInput = ref<HTMLInputElement>();

// Dynamic label based on entry type
const dynamicLabel = computed(() => {
  switch (props.entryType) {
    case 'ate':
      return 'I ate...';
    case 'drank':
      return 'I drank...';
    case 'exercised':
      return 'I exercised...';
    case 'did':
      return 'I did...';
    case 'slept':
      return 'I slept...';
    case 'felt':
      return 'I feel...';
    default:
      return 'I ate...';
  }
});

// Dynamic label for noticed content
const noticedLabel = computed(() => {
  return props.entryType === 'felt' ? 'I noticed...' : 'I felt...';
});

// Entry icon based on type
const entryIcon = computed(() => {
  switch (props.entryType) {
    case 'ate':
      return 'fa fa-cutlery';
    case 'slept':
      return 'fa fa-bed';
    default:
      return 'fa fa-cutlery';
  }
});

const isEditingDateTime = ref(false);

const dateValue = computed({
  get: () => {
    return formData.value.entry_datetime.split('T')[0];
  },
  set: (value) => {
    const [hours, minutes] = timeValue.value.split(':').map(Number);
    const newDate = new Date(value);
    newDate.setHours(hours, minutes, 0, 0);
    formData.value.entry_datetime = newDate.toISOString();
  }
});

const timeValue = computed({
  get: () => {
    const date = new Date(formData.value.entry_datetime);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  },
  set: (value) => {
    const datePart = dateValue.value;
    const [hours, minutes] = value.split(':').map(Number);
    const newDate = new Date(datePart);
    newDate.setHours(hours, minutes, 0, 0);
    formData.value.entry_datetime = newDate.toISOString();
  }
});

const checkBlur = (_e: FocusEvent) => {
  setTimeout(() => {
    const dateInput = document.querySelector('input[type="date"]');
    const timeInput = document.querySelector('input[type="time"]');
    if (!dateInput?.contains(document.activeElement) && !timeInput?.contains(document.activeElement)) {
      isEditingDateTime.value = false;
    }
  }, 100);
};

// Format the current date and time
const formattedDateTime = computed(() => {
  const date = new Date(formData.value.entry_datetime);
  return date.toLocaleString('default', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
});

const isFormValid = computed(() => {
  return !!formData.value.ate_content?.trim();
});

const buttonText = computed(() => props.isEditing ? 'Save Changes' : 'Submit');

const existingImageUrl = computed(() => {
  if (removeExistingImageFlag.value) return null;
  return props.existingEntry?.file?.file_path || null;
});

// Image handling methods
const handleImageSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  
  if (file) {
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      formError.value = 'Image file size must be less than 10MB';
      return;
    }
    
    selectedImage.value = file;
    imagePreviewUrl.value = URL.createObjectURL(file);
    formError.value = null;
  }
};

  const removeSelectedImage = () => {
    selectedImage.value = null;
    imagePreviewUrl.value = '';
    
    if (fileInput.value) {
      fileInput.value.value = '';
    }
  };

const removeExistingImage = () => {
  removeExistingImageFlag.value = true;
};

const closeDialog = () => {
  dialog.value = false;
  formError.value = null;
  removeSelectedImage();
  removeExistingImageFlag.value = false;
  
  if (!props.isEditing) {
    formData.value = {
      entry_datetime: new Date().toISOString(),
      entry_type: '',
      ate_content: '',
      noticed_content: ''
    };
  }
};

const submitForm = async () => {
  if (!isFormValid.value) return;

  isSubmitting.value = true;
  formError.value = null;

  try {
    formData.value.entry_type = props.entryType || undefined;
    
    // Create FormData if we have an image to upload
    const hasImageToUpload = selectedImage.value || removeExistingImageFlag.value;
    
    if (hasImageToUpload) {
      const formDataToSend = new FormData();
      formDataToSend.append('entry_datetime', formData.value.entry_datetime);
      formDataToSend.append('entry_type', formData.value.entry_type || '');
      formDataToSend.append('ate_content', formData.value.ate_content || '');
      formDataToSend.append('noticed_content', formData.value.noticed_content || '');
      
      if (selectedImage.value) {
        formDataToSend.append('image', selectedImage.value);
      }
      
      if (props.isEditing && props.existingEntry?.id) {
        await healthDiaryService.update(props.existingEntry.id, formDataToSend);
        emit('entry-updated', formData.value.entry_datetime);
      } else {
        await healthDiaryService.create(formDataToSend);
        emit('entry-created', formData.value.entry_datetime);
      }
    } else {
      // No image changes, send as regular JSON
      if (props.isEditing && props.existingEntry?.id) {
        await healthDiaryService.update(props.existingEntry.id, formData.value);
        emit('entry-updated', formData.value.entry_datetime);
      } else {
        await healthDiaryService.create(formData.value);
        emit('entry-created', formData.value.entry_datetime);
      }
    }
    
    closeDialog();
  } catch (err: any) {
    console.error('Error saving diary entry:', err);
    formError.value = err.response?.data?.message || 'Failed to save diary entry. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};

// Watch for existing entry changes to update form data
watch(() => props.existingEntry, (newEntry) => {
  if (newEntry) {
    formData.value = {
      entry_datetime: newEntry.entry_datetime,
      entry_type: newEntry.entry_type,
      ate_content: newEntry.ate_content || '',
      noticed_content: newEntry.noticed_content || ''
    };
  }
}, { immediate: true });

// Watch for entry type changes to update form data
watch(() => props.entryType, (newType) => {
  if (newType) {
    formData.value.entry_type = newType;
  }
}, { immediate: true });
</script>

<style scoped>
.diary-entry-form {
  border-radius: 16px;
}

.practitioner-comment {
  border-radius: 8px;
}

:deep(.v-field) {
  border-radius: 12px !important;
}

:deep(.v-textarea .v-field__input) {
  min-height: 120px !important;
  padding-top: 12px;
}

:deep(.v-label) {
  font-size: 14px;
  font-weight: 500;
}

:deep(.v-btn) {
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
}

.cursor-pointer {
  cursor: pointer;
}

.image-preview-container {
  position: relative;
  display: inline-block;
  border-radius: 8px;
  overflow: hidden;
}

.image-preview,
.existing-image {
  max-width: 200px;
  max-height: 200px;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.remove-image-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(255, 255, 255, 0.95) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
  min-width: 24px !important;
  width: 24px !important;
  height: 24px !important;
  font-size: 14px !important;
  font-weight: bold !important;
  line-height: 1 !important;
  color: black !important;
}
</style> 